import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";

import ControlledTable from "../../../manager/components/controlled-table/controlled-table";
import { IColumn, ISortedBy, ITableRowData } from "../../../manager/components/data-table/data-table";
import Page from "../../../manager/components/page/page";
import { noop } from "../../../manager/utils";
import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import AuthContext from "../../../shared/context/auth-context";
import { useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";
import { getAllDataNonTable, postSingleNonTable } from "../../../shared/utils/api";
import { createUUID, validateLoadingStatusNew } from "../../../shared/utils/utils";
import "./user-group.scss";
import { usePrompt } from "../../../shared/utils/use-prompt";
import SelectUserModal from "./select-user-modal";

const columns: IColumn[] = [
  {
    key: "name",
    displayName: "First Name",
    dataType: {
      type: "text",
    },
    sortable: true
  },
  {
    key: "last_name",
    displayName: "Last Name",
    dataType: {
      type: "text",
    },
    sortable: true
  },
  {
    key: "email",
    displayName: "Email Address",
    dataType: {
      type: "text",
    },
    sortable: true
  }
];

interface IUserGroup {
  id: string;
  name: string;
  users: ITableRowData[];
}

const UserGroupNew = () => {
  const [data, setData] = useState<IUserGroup>({id: createUUID(), name: "", users: []});
  const [showSelectUserModal, setShowSelectUserModal] = useState(false);
  const [sortedBy, setSortedBy] = useState<ISortedBy>({column: "email", direction: "asc"});
  const [isDirty, setIsDirty] = useState(false);
  const { flag, setFlag, next } = usePrompt(isDirty);

  const authCtx = useContext(AuthContext);
  const params = useParams();

  const { sendRequest: getUserGroup, status: getUserGroupStatus, data: getUserGroupData, error: getUserGroupError } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "default");
  const { sendRequest: postUserGroup, status: postUserGroupStatus, data: postUserGroupData, error: postUserGroupError } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "userGroup");
  
  useEffect(() => {
    if (params.id) {
      getUserGroup(`user_group/${params.id}?order_by=${sortedBy.column} ${sortedBy.direction}`);
    }
  }, [sortedBy]);

  useEffect(() => {
    if (getUserGroupStatus === "completed" && !getUserGroupError && getUserGroupData) {
      setData({...getUserGroupData});
    }
  }, [getUserGroupStatus, getUserGroupData, getUserGroupError]);

  useEffect(() => {
    if (postUserGroupStatus === "completed" && !postUserGroupError && postUserGroupData) {
      setData(postUserGroupData);
      setIsDirty(false);
    }
  }, [postUserGroupStatus, postUserGroupData, postUserGroupError]);
  
  const handleOnDeleted = (selectedIds: string[]) => {
    const newData = {...data};
    newData.users = newData.users.filter((user: ITableRowData) => !selectedIds.includes(user.id));
    setData(newData);
    postUserGroup(`user_group/${data.id}`, {
      id: data.id,
      name: data.name,
      user_ids: newData.users.map((user: ITableRowData) => user.id)
    });
  };

  const handleOnUsersChanged = (selectedIds: string[]) => {
    setShowSelectUserModal(false);
    postUserGroup(`user_group/${data.id}`, {
      id: data.id,
      name: data.name,
      user_ids: selectedIds
    });
  };

  const handleOnSorted = (sortedByCol: ISortedBy) => {
    setSortedBy(sortedByCol);
  };

  const handleOnSaveClicked = () => {
    postUserGroup(`user_group/${data.id}`, {
      id: data.id,
      name: data.name,
      user_ids: data.users.map((user: ITableRowData) => user.id)
    });
  };

  return (
    <Page 
      breadCrumbItems={[
        {name: "User groups", url: "/user-groups"},
        {name: data?.name || "Create new User Group"}
      ]}  
      title={{
        text: data?.name || "",
        tooltip: `${data?.name ? "Edit" : "Create"} a user group`,
        placeholder: "User group name",
        onTitleEdited: (title: string) => {
          setData({...data, name: title});
          setIsDirty(true);
        }
      }}
    >
      <div className="userGroup">
        {validateLoadingStatusNew([postUserGroupStatus, getUserGroupStatus]) && <Spinner />}
        <SelectUserModal 
          show={showSelectUserModal} 
          selectedIds={data.users.map((user: ITableRowData) => user.id)} 
          onClosed={() => setShowSelectUserModal(false)} 
          onSubmit={handleOnUsersChanged} 
        />
        <ControlledTable
          columns={columns}
          data={data.users}
          selectionMode="multi"
          onDeleted={handleOnDeleted}
          onNewClicked={() => setShowSelectUserModal(true)}
          onRowClick={() => setShowSelectUserModal(true)}
          onSaveClicked={handleOnSaveClicked}
          onSorted={handleOnSorted}
          onScroll={noop}
        />
      </div>
      {flag && <ConfirmationPopup 
        show={flag} 
        handleSaveClick={() => {
          handleOnSaveClicked();
          next(); 
        }}
        handlePopupClosed={() => {
          setFlag(false);
        }}
        handleContinue={() => { 
          next(); 
          setFlag(false); 
        }} />
      }
    </Page>
  );
};

export default UserGroupNew;
